import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { http } from '@/lib/http'
import { useAuthStore, type UserSex } from '@/stores/auth'
import type {
  AppClientIdentityCredentialVo,
  AppClientIdentityLinkVo,
  AppClientIdentityMergeBo,
  AppClientPhoneBindBo,
  AppClientPhoneChangeBo,
  AppClientPhoneVo,
  AppClientSmsCodeBo,
  AppClientWechatLinkSessionBo,
  AppClientWechatLinkSessionVo,
  AppProfileMeVo,
  AppUserMeVo,
  AppUserLiteVo,
  AppUserProfileUpdateBo,
  AppUserStatsVo,
  AvatarVo,
  ContactTypeVo,
  LegalContactCatalogItemVo,
  LegalUserContactSaveBo,
  LegalUserContactVo,
  WechatIdentityLinkChannel,
  WechatWebAuthorizeUrlVo,
} from '@/types/api'

const identityCredentialsQueryKey = ['user', 'identity', 'credentials'] as const
const currentProfileQueryKey = ['user', 'profile'] as const
const profileContactsQueryKey = [...currentProfileQueryKey, 'contacts'] as const
const profileContactTypesQueryKey = [...currentProfileQueryKey, 'contact-types'] as const
const WECHAT_IDENTITY_LINK_POLL_INTERVAL_MS = 1_800

export type ClientPhoneSmsScene =
  | 'phone_bind'
  | 'phone_change_old'
  | 'phone_change_new'
  | 'web_phone_login'

/* ---------- 当前登录用户基础资料 ---------- */

/** 获取当前用户聚合资料。 */
export async function fetchClientProfile(): Promise<AppProfileMeVo> {
  return http.get<AppProfileMeVo>('/client/legal/profile/me')
}

function requireProfileUser(profile: AppProfileMeVo): AppUserMeVo {
  if (!profile.user) {
    throw new Error('当前用户资料响应不完整')
  }
  return profile.user
}

/**
 * 获取当前登录用户基础信息。
 * 保留原函数返回类型，避免登录流程与业务调用方感知聚合响应结构。
 */
export async function fetchClientUserInfo(): Promise<AppUserMeVo> {
  return requireProfileUser(await fetchClientProfile())
}

export async function fetchUserInfo(): Promise<AppUserMeVo> {
  return fetchClientUserInfo()
}

export function useUserInfoQuery(enabled = true) {
  return useQuery({
    queryKey: ['user', 'info'],
    queryFn: fetchClientUserInfo,
    enabled,
    staleTime: 5 * 60_000,
  })
}

/* ---------- 用户资料 (client/legal/profile/me) ---------- */

export function useProfileMe(enabled = true) {
  return useQuery({
    queryKey: currentProfileQueryKey,
    queryFn: fetchClientProfile,
    enabled,
    staleTime: 60_000,
  })
}

export function useUserProfile() {
  return useQuery({
    queryKey: currentProfileQueryKey,
    queryFn: fetchClientProfile,
    select: requireProfileUser,
    staleTime: 60_000,
  })
}

/** 批量查询 C 端用户轻量资料 */
export function useUserLiteProfiles(userIds: string[], opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['user', 'lite-profiles', userIds],
    queryFn: () =>
      http.get<AppUserLiteVo[]>('/client/legal/profile/users', {
        params: { ids: userIds.join(',') },
      }),
    enabled: (opts.enabled ?? true) && userIds.length > 0,
    staleTime: 5 * 60_000,
  })
}

/* ---------- 用户统计 ---------- */

export function useUserStats() {
  return useQuery({
    queryKey: ['user', 'stats'],
    queryFn: () => http.get<AppUserStatsVo>('/client/legal/user/stats'),
    staleTime: 30_000,
  })
}

/* ---------- 编辑资料 ---------- */

export function useUpdateUserProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['user', 'profile', 'update'],
    mutationFn: (input: AppUserProfileUpdateBo) =>
      http.put<void>('/client/legal/user/profile', input),
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: currentProfileQueryKey })
      qc.invalidateQueries({ queryKey: ['user', 'info'] })
      useAuthStore.getState().patchUserInfo({
        ...(input.nickName === undefined
          ? {}
          : { nickname: input.nickName, username: input.nickName }),
        ...(input.email === undefined ? {} : { email: input.email }),
        ...(input.sex === undefined ? {} : { sex: input.sex as UserSex }),
      })
    },
  })
}

/* ---------- 补充联系方式 ---------- */

export function useProfileContactTypes(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: profileContactTypesQueryKey,
    queryFn: () => http.get<ContactTypeVo[]>('/client/legal/profile/contact-types'),
    enabled: opts.enabled ?? true,
    staleTime: 10 * 60_000,
  })
}

export function useProfileContacts(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: profileContactsQueryKey,
    queryFn: () => http.get<LegalContactCatalogItemVo[]>('/client/legal/profile/contacts'),
    enabled: opts.enabled ?? true,
    staleTime: 0,
  })
}

function invalidateProfileContacts(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: currentProfileQueryKey })
}

export function useCreateProfileContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['user', 'profile', 'contacts', 'create'],
    mutationFn: (body: LegalUserContactSaveBo) =>
      http.post<LegalUserContactVo>('/client/legal/profile/contacts', body),
    onSuccess: () => invalidateProfileContacts(qc),
  })
}

export function useUpdateProfileContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['user', 'profile', 'contacts', 'update'],
    mutationFn: ({ id, body }: { id: string; body: LegalUserContactSaveBo }) =>
      http.put<LegalUserContactVo>(
        `/client/legal/profile/contacts/${encodeURIComponent(id)}`,
        body,
      ),
    onSuccess: () => invalidateProfileContacts(qc),
  })
}

export function useDeleteProfileContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['user', 'profile', 'contacts', 'delete'],
    mutationFn: (id: string) =>
      http.delete<void>(`/client/legal/profile/contacts/${encodeURIComponent(id)}`),
    onSuccess: () => invalidateProfileContacts(qc),
  })
}

/* ---------- 手机号绑定 / 换绑 ---------- */

interface SendClientPhoneSmsInput {
  scene: ClientPhoneSmsScene
  phonenumber?: string
}

interface BindClientPhoneInput {
  phonenumber: string
  smsCode: string
}

interface ChangeClientPhoneInput {
  newPhonenumber: string
  oldSmsCode: string
  newSmsCode: string
}

export function useSendClientPhoneSmsCode() {
  return useMutation({
    mutationKey: ['user', 'phone', 'sms', 'send'],
    mutationFn: async (input: SendClientPhoneSmsInput) => {
      const body: AppClientSmsCodeBo = {
        scene: input.scene,
        phonenumber: input.phonenumber,
      }
      return http.post<void>('/client/system/user/phone/sms/code', body)
    },
  })
}

export function useBindClientPhone() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['user', 'phone', 'bind'],
    mutationFn: async (input: BindClientPhoneInput) => {
      const body: AppClientPhoneBindBo = {
        phonenumber: input.phonenumber,
        smsCode: input.smsCode,
      }
      return http.post<AppClientPhoneVo>('/client/system/user/phone/sms/bind', body)
    },
    onSuccess: (phone) => {
      if (!requiresIdentityMerge(phone)) {
        syncUserPhone(qc, phone)
      }
    },
  })
}

export function useChangeClientPhone() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['user', 'phone', 'change'],
    mutationFn: async (input: ChangeClientPhoneInput) => {
      const body: AppClientPhoneChangeBo = {
        newPhonenumber: input.newPhonenumber,
        oldSmsCode: input.oldSmsCode,
        newSmsCode: input.newSmsCode,
      }
      return http.post<AppClientPhoneVo>('/client/system/user/phone/sms/change', body)
    },
    onSuccess: (phone) => {
      if (!requiresIdentityMerge(phone)) {
        syncUserPhone(qc, phone)
      }
    },
  })
}

function syncUserPhone(qc: QueryClient, phone: AppClientPhoneVo) {
  qc.invalidateQueries({ queryKey: currentProfileQueryKey })
  qc.invalidateQueries({ queryKey: ['user', 'info'] })
  qc.invalidateQueries({ queryKey: identityCredentialsQueryKey })
  if (phone.phonenumber) {
    useAuthStore.getState().patchUserInfo({ phonenumber: phone.phonenumber })
  }
}

async function refreshClientUserSnapshot(qc: QueryClient) {
  try {
    const profile = await fetchClientProfile()
    const info = requireProfileUser(profile)
    qc.setQueryData(['user', 'info'], info)
    qc.setQueryData(currentProfileQueryKey, profile)
    const auth = useAuthStore.getState()
    auth.setRoles(Array.from(new Set((info.roles ?? []).filter(Boolean))))
    auth.setUserInfo({
      userId: String(info.userId ?? ''),
      username: info.nickName ?? '',
      nickname: info.nickName ?? '',
      avatar: info.avatar,
      phonenumber: info.phonenumber,
      email: info.email,
      sex: normalizeUserSex(info.sex),
    })
  } catch {
    // 合并已成功时不因资料刷新失败回滚前端成功态，保留失效标记供下次查询自愈
  }
}

function normalizeUserSex(value: string | undefined): UserSex | undefined {
  return value === '0' || value === '1' || value === '2' ? value : undefined
}

/* ---------- 第三方身份绑定 / 解绑 / 账号合并 ---------- */

interface CreateWechatIdentityLinkInput {
  channel: WechatIdentityLinkChannel
  redirect: string
}

interface ConfirmClientIdentityMergeInput {
  mergeToken: string
  /** 手机号绑定触发合并时，用于立即同步本地登录用户摘要。 */
  phonenumber?: string
}

export function requiresIdentityMerge(
  result: Pick<AppClientPhoneVo, 'bindingStatus' | 'mergeRequired'>,
): boolean {
  return result.bindingStatus === 'MERGE_REQUIRED' || result.mergeRequired === true
}

export function useClientIdentityCredentials(enabled = true) {
  return useQuery({
    queryKey: identityCredentialsQueryKey,
    queryFn: () =>
      http.get<AppClientIdentityCredentialVo[]>('/client/system/user/identity/credential/list'),
    enabled,
    staleTime: 30_000,
  })
}

export function useCreateWechatIdentityLinkSession() {
  return useMutation({
    mutationKey: ['user', 'identity', 'wechat', 'link-session'],
    mutationFn: async ({ channel, redirect }: CreateWechatIdentityLinkInput) => {
      const body: AppClientWechatLinkSessionBo = {
        channel,
        redirect,
      }
      const result = await http.post<WechatWebAuthorizeUrlVo>(
        '/client/system/user/identity/wechat/link-session',
        body,
      )
      if (!result.authorizeUrl || !result.linkSessionId) {
        throw new Error('微信绑定会话响应不完整，请稍后重试')
      }
      return result
    },
  })
}

export function useWechatIdentityLinkSession(linkSessionId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['user', 'identity', 'wechat', 'link-session', linkSessionId],
    queryFn: () => {
      if (!linkSessionId) throw new Error('微信绑定会话编号缺失')
      return http.get<AppClientWechatLinkSessionVo>(
        `/client/system/user/identity/wechat/link-session/${encodeURIComponent(linkSessionId ?? '')}`,
      )
    },
    enabled: enabled && Boolean(linkSessionId),
    refetchInterval: (query) =>
      query.state.data?.status === 'CREATED' ? WECHAT_IDENTITY_LINK_POLL_INTERVAL_MS : false,
  })
}

export function useConfirmWechatIdentityLinkSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['user', 'identity', 'wechat', 'link-session', 'confirm'],
    mutationFn: (linkSessionId: string) =>
      http.post<AppClientIdentityLinkVo>(
        `/client/system/user/identity/wechat/link-session/${encodeURIComponent(linkSessionId)}/confirm`,
      ),
    onSuccess: (result) => {
      if (!requiresIdentityMerge(result)) {
        qc.invalidateQueries({ queryKey: identityCredentialsQueryKey })
      }
    },
  })
}

export function useConfirmClientIdentityMerge() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['user', 'identity', 'merge', 'confirm'],
    mutationFn: async ({ mergeToken }: ConfirmClientIdentityMergeInput) => {
      const body: AppClientIdentityMergeBo = { mergeToken }
      return http.post<AppClientIdentityLinkVo>('/client/system/user/identity/merge/confirm', body)
    },
    onSuccess: (_result, input) => {
      qc.invalidateQueries({ queryKey: identityCredentialsQueryKey })
      qc.invalidateQueries({ queryKey: currentProfileQueryKey })
      qc.invalidateQueries({ queryKey: ['user', 'info'] })
      if (input.phonenumber) {
        useAuthStore.getState().patchUserInfo({ phonenumber: input.phonenumber })
      }
      void refreshClientUserSnapshot(qc)
    },
  })
}

export function useUnbindClientIdentityCredential() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['user', 'identity', 'credential', 'unbind'],
    mutationFn: (credentialId: string) =>
      http.delete<void>(
        `/client/system/user/identity/credential/${encodeURIComponent(credentialId)}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: identityCredentialsQueryKey })
    },
  })
}

/* ---------- 头像上传 ---------- */

export function useUploadAvatar() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['user', 'avatar', 'upload'],
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('avatarfile', file)
      return http.post<AvatarVo>('/client/legal/user/avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    onSuccess: (avatar) => {
      qc.invalidateQueries({ queryKey: currentProfileQueryKey })
      qc.invalidateQueries({ queryKey: ['user', 'info'] })
      if (avatar.imgUrl) {
        useAuthStore.getState().patchUserInfo({ avatar: avatar.imgUrl })
      }
    },
  })
}
